import { useState, useCallback } from 'react';
import { useLongPress } from 'use-long-press';
import { LayoutAlternative } from '@thive/fddm-types';

import { useNavigation } from '@/modules/common/hooks';

import { useFloorplanAdminStore } from '../store';

export const useFloorplanGridItem = (
  layoutAlternative: LayoutAlternative,
  isSelected: boolean,
  isBeingDeleted: boolean,
  openDuplicator: () => void,
) => {
  const { goToBuilder } = useNavigation();
  const [itemHover, setItemHover] = useState(false);
  const { alternativeId } = layoutAlternative;

  const handleFloorplanClick = useCallback(
    (e) => {
      e.stopPropagation();
      const { selectedFloorplanIdsState, setSelectedFloorplanIdsState } = useFloorplanAdminStore.getState();

      if (e.shiftKey) {
        const newSelectedIds = selectedFloorplanIdsState.includes(alternativeId)
          ? selectedFloorplanIdsState.filter((id) => id !== alternativeId)
          : [...selectedFloorplanIdsState, alternativeId];
        setSelectedFloorplanIdsState(newSelectedIds);

        return;
      }

      setSelectedFloorplanIdsState([alternativeId]);
    },
    [alternativeId],
  );

  const onDuplicate = useCallback(
    (e) => {
      e.stopPropagation();
      useFloorplanAdminStore.getState().setFloorplanIdBeingDuplicatedState(alternativeId);
      openDuplicator();
    },
    [openDuplicator, alternativeId],
  );

  const onDoubleClick = useCallback(() => {
    goToBuilder(layoutAlternative.alternativeId, layoutAlternative.lastRevisionId);
  }, [goToBuilder, layoutAlternative]);

  const bindLongPress = useLongPress(() => {
    onDoubleClick();
  });

  const onMouseOver = useCallback(() => setItemHover(true), [setItemHover]);
  const onMouseLeave = useCallback(() => setItemHover(false), [setItemHover]);

  const onContextMenuHandler = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();

      if (!isSelected) {
        useFloorplanAdminStore.getState().setSelectedFloorplanIdsState([alternativeId]);
      }

      useFloorplanAdminStore.getState().setContextMenuPosition({
        x: e.clientX,
        y: e.clientY,
      });
    },
    [isSelected, alternativeId],
  );

  return {
    name: layoutAlternative.name,
    itemHover,
    handleFloorplanClick,
    thumbnail: layoutAlternative.lastThumbnail,
    bindLongPress,
    onMouseOver,
    onMouseLeave,
    onDoubleClick,
    onContextMenuHandler,
    isSelected,
    isBeingDeleted,
    onDuplicate,
  };
};
